import { HubConnection, HubConnectionState } from "@microsoft/signalr";
import { useEffect, useState } from "react";
import { DeviceStatus } from "Infrastructure/Models/Messages/Dto/DeviceStatus";
import { logError } from "Infrastructure/Utils/LoggingUtils";
import {
	mergeDeviceStatuses,
	ProductStatusDeviceModel,
} from "Infrastructure/Utils/Nsure/ProductUtils";

export const getProductModelDeviceNames = (
	deviceStatuses: DeviceStatus[] | null,
	deviceStatus?: DeviceStatus | undefined
): string[] =>
	deviceStatuses
		?.filter(
			(x) => !x.isSelfInsurance && (!!deviceStatus ? x === deviceStatus : true)
		)
		.map((x) => x.deviceName) ?? [];

const useProductDeviceModel = (
	hubConnection: HubConnection | null | undefined,
	deviceStatuses: DeviceStatus[] | null,
	deviceStatus?: DeviceStatus | undefined
) => {
	const [productDeviceModel, setProductDeviceModel] =
		useState<ProductStatusDeviceModel | null>(null);
	const [isLoading, setIsLoading] = useState<boolean>(true);

	// biome-ignore lint/correctness/useExhaustiveDependencies: hubConnection is stable for the dialog lifetime
	useEffect(() => {
		if (!deviceStatuses) {
			setProductDeviceModel(null);
			setIsLoading(false);
			return;
		}
		setIsLoading(true);
		const fallback = () => {
			setProductDeviceModel(mergeDeviceStatuses(deviceStatuses, deviceStatus));
			setIsLoading(false);
		};
		const deviceNames = getProductModelDeviceNames(deviceStatuses, deviceStatus);
		if (
			deviceNames.length === 0 ||
			!hubConnection ||
			hubConnection.state !== HubConnectionState.Connected
		) {
			fallback();
			return;
		}
		hubConnection
			.invoke<ProductStatusDeviceModel>("getProductDeviceModelRequest", deviceNames)
			.then((result) => {
				setProductDeviceModel(result ?? null);
				setIsLoading(false);
			})
			.catch((error: Error) => {
				logError(error);
				fallback();
			});
	}, [deviceStatuses, deviceStatus]);

	return { productDeviceModel, isLoading };
};

export default useProductDeviceModel;
